import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import ProfileBlockerModal from "@/components/ProfileBlockerModal";
import CancellationPaymentBlockerModal from "@/components/CancellationPaymentBlockerModal";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  MapPin,
  Calendar,
  Clock,
  DollarSign,
  Briefcase,
  Send,
  Building2,
  ArrowLeft,
  User,
  FileText,
  CheckCircle,
  Loader2,
  Share2,
  Copy,
  Check
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

const JobDetails = ({ onLogout }) => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');
  const isAuthenticated = !!user.id;

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userStatus, setUserStatus] = useState('');
  const [showProfileBlocker, setShowProfileBlocker] = useState(false);
  const [outstandingCancellationFees, setOutstandingCancellationFees] = useState(null);
  const [showCancellationBlocker, setShowCancellationBlocker] = useState(false);

  // Application states
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [notifyIfUnsuccessful, setNotifyIfUnsuccessful] = useState(false);
  const [submittingApplication, setSubmittingApplication] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  // Fetch job details
  useEffect(() => {
    const fetchJob = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/jobs/details/${jobId}`
        );
        setJob(response.data);

        // Check if user has already applied
        if (isAuthenticated && Array.isArray(response.data.applicants)) {
          const userApplied = response.data.applicants.some(
            app => app.user_id === user.id || app.email === user.email
          );
          setHasApplied(userApplied);
        }
      } catch (err) {
        console.error('Error fetching job:', err);
        setError(err.response?.data?.detail || 'Job not found');
      } finally {
        setLoading(false);
      }
    };

    if (jobId) {
      fetchJob();
    }
  }, [jobId, user.id, user.email, isAuthenticated]);

  // Fetch user status if authenticated
  useEffect(() => {
    const fetchUserStatus = async () => {
      if (user.id) {
        try {
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/profile/${user.id}`);
          const dbStatus = (response.data.status || '').toUpperCase();
          setUserStatus(dbStatus);
        } catch (error) {
          console.error('Error fetching user status:', error);
        }
      }
      if (user.email) {
        try {
          const feesResponse = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/user/outstanding-cancellation-fees`,
            { params: { user_email: user.email } }
          );
          if (feesResponse.data.has_outstanding_fees) {
            setOutstandingCancellationFees(feesResponse.data);
            setShowCancellationBlocker(true);
          }
        } catch (err) {
          console.error('Failed to check cancellation fees:', err);
        }
      }
    };
    if (isAuthenticated) {
      fetchUserStatus();
    }
  }, [user.id, isAuthenticated]);

  // Update document title and meta tags when job loads
  useEffect(() => {
    if (job) {
      const title = `${job.title || 'Job Opportunity'} | Shootzu`;
      document.title = title;

      // Update meta description
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content',
          `${job.type || 'Crew'} job in ${job.location || 'Australia'} - ${job.hours || ''}hrs @ $${job.ratePerHour || job.pay_rate || 0}/hr. Apply now on Shootzu!`
        );
      }
    }

    return () => {
      document.title = 'Shootzu | Find Crew, Be Crew';
    };
  }, [job]);

  const handleShareJob = async () => {
    const shareUrl = `${window.location.origin}/jobs/${jobId}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      toast.success('Job link copied to clipboard!');

      setTimeout(() => setLinkCopied(false), 3000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopied(true);
      toast.success('Job link copied to clipboard!');
      setTimeout(() => setLinkCopied(false), 3000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getJobTypeColor = (type) => {
    const colors = {
      photographer: 'from-pink-500 to-rose-500',
      videographer: 'from-blue-500 to-cyan-500',
      editor: 'from-purple-500 to-indigo-500',
      assistant: 'from-green-500 to-emerald-500',
      drone_operator: 'from-orange-500 to-amber-500'
    };
    return colors[type] || 'from-purple-500 to-fuchsia-500';
  };

  const getCrewPayout = (amount) => {
    return Math.floor(amount * 0.98); // 2% Shootzu fee
  };

  const handleApplyClick = () => {
    if (!isAuthenticated) {
      // Store the current URL to redirect back after login
      localStorage.setItem('redirectAfterLogin', `/jobs/${jobId}`);
      navigate('/login');
      return;
    }

    // Check if user is the job owner
    const isJobOwner = job?.owner?.id === user.id ||
                       job?.owner?.email === user.email ||
                       job?.owner_id === user.id ||
                       job?.client_email === user.email;
    if (isJobOwner) {
      toast.error("You cannot apply to your own job");
      return;
    }

    // Check user status
    if (userStatus !== 'APPROVED') {
      setShowProfileBlocker(true);
      return;
    }

    setShowApplyModal(true);
  };

  const handleSubmitApplication = async () => {
    if (!job || !user.id) return;

    const token = localStorage.getItem('shootzu_token');
    if (!token) {
      toast.error('Please log in to apply for jobs');
      return;
    }

    setSubmittingApplication(true);
    try {
      const applicantName = user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown';

      // Determine the role to send
      const isAdvanced = (job?.dates && job.dates.length > 1) || (job?.crew_members && job.crew_members.length > 1);
      const roleToSend = isAdvanced && job?.crew_members?.length > 0
        ? selectedRole
        : (job?.type || job?.job_type || null);

      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/apply`, {
        job_id: job.id,
        job_title: job.title,
        applicant_name: applicantName,
        applicant_email: user.email,
        application_message: applicationMessage,
        notify_if_unsuccessful: notifyIfUnsuccessful,
        job_owner_name: job.owner?.name || 'Job Owner',
        job_owner_email: job.owner?.email || job.owner_email || job.client_email,
        job_date: job.jobDate || job.date || 'TBD',
        role: roleToSend
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setShowApplyModal(false);
      setShowConfirmationModal(true);
      setHasApplied(true);
      setApplicationMessage('');
      setNotifyIfUnsuccessful(false);
      setSelectedRole(null);
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmittingApplication(false);
    }
  };

  // Public header for non-authenticated users - Fixed position like other public pages
  const PublicHeader = () => (
    <header className="fixed top-0 w-full bg-gradient-to-r from-purple-900 via-purple-800 to-indigo-900 text-white py-4 px-6 shadow-lg z-50">
      <div className="max-w-4xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/assets/shootzu-logo-white.png"
            alt="Shootzu"
            className="h-10 sm:h-12"
          />
        </Link>
        <div className="flex items-center gap-3">
          <Button
            onClick={handleShareJob}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10"
          >
            {linkCopied ? (
              <Check className="w-4 h-4 mr-2" />
            ) : (
              <Share2 className="w-4 h-4 mr-2" />
            )}
            {linkCopied ? 'Copied!' : 'Share'}
          </Button>
          <Link to="/login">
            <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
              Log In
            </Button>
          </Link>
          <Link to="/signup">
            <Button size="sm" className="bg-white text-purple-900 hover:bg-gray-100 font-semibold">
              Sign Up
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );

  // Loading state
  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0f0a19]' : 'bg-gray-50'}`}>
        {isAuthenticated ? (
          <Sidebar onLogout={onLogout} />
        ) : (
          <PublicHeader />
        )}
        <div className={isAuthenticated ? "lg:ml-72" : ""}>
          {isAuthenticated && <Header onLogout={onLogout} />}
          <main className={`${isAuthenticated ? 'p-4 sm:p-6' : 'px-4 sm:px-8 pt-24 pb-6 max-w-4xl mx-auto'}`}>
            <div className="flex items-center justify-center h-64">
              <Loader2 className={`w-8 h-8 animate-spin ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0f0a19]' : 'bg-gray-50'}`}>
        {isAuthenticated ? (
          <Sidebar onLogout={onLogout} />
        ) : (
          <PublicHeader />
        )}
        <div className={isAuthenticated ? "lg:ml-72" : ""}>
          {isAuthenticated && <Header onLogout={onLogout} />}
          <main className={`${isAuthenticated ? 'p-4 sm:p-6' : 'px-4 sm:px-8 pt-24 pb-6 max-w-4xl mx-auto'}`}>
            <Card className={isDark ? 'bg-[#1e1534] border-purple-500/20' : ''}>
              <CardContent className="p-8 text-center">
                <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Job Not Found
                </h2>
                <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {error}
                </p>
                <Button onClick={() => navigate(isAuthenticated ? '/find-jobs' : '/')}>
                  {isAuthenticated ? 'Browse Jobs' : 'Go Home'}
                </Button>
              </CardContent>
            </Card>
          </main>
        </div>
      </div>
    );
  }

  // Check if job is still accepting applications
  const canApply = job && (job.status === 'SEEKING_APPLICANTS' || job.status === 'PARTIALLY_HIRED' || job.status === 'open' || job.status === 'active');
  const isAdvancedJob = (job?.dates && job.dates.length > 1) || (job?.crew_members && job.crew_members.length > 1);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0f0a19]' : 'bg-gray-50'}`}>
      {isAuthenticated ? (
        <Sidebar onLogout={onLogout} />
      ) : (
        <PublicHeader />
      )}

      <div className={isAuthenticated ? "lg:ml-72" : ""}>
        {isAuthenticated && <Header onLogout={onLogout} />}

        <main className={`${isAuthenticated ? 'p-4 sm:p-6' : 'px-4 sm:px-8 pt-24 pb-6 max-w-4xl mx-auto'}`}>
          {/* Back Button & Share */}
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate(isAuthenticated ? '/find-jobs' : '/')}
              className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {isAuthenticated ? 'Back to Jobs' : 'Back to Home'}
            </Button>
            {isAuthenticated && (
              <Button
                onClick={handleShareJob}
                variant="outline"
                size="sm"
                className={`${isDark ? 'border-purple-500/30 text-purple-400 hover:bg-purple-500/10' : 'border-purple-300 text-purple-700 hover:bg-purple-50'}`}
              >
                {linkCopied ? (
                  <Check className="w-4 h-4 mr-2 text-green-500" />
                ) : (
                  <Share2 className="w-4 h-4 mr-2" />
                )}
                {linkCopied ? 'Link Copied!' : 'Share Job'}
              </Button>
            )}
          </div>

          {/* Job Card */}
          <Card className={`overflow-hidden ${isDark ? 'bg-[#1e1534] border-purple-500/20' : 'bg-white'}`}>
            {/* Header with Gradient */}
            <div className={`bg-gradient-to-r ${getJobTypeColor(job?.type || job?.job_type)} p-6 relative`}>
              <div className="absolute inset-0 bg-black/20"></div>
              <div className="relative">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30 font-mono text-xs px-3 py-1">
                    #{job?.jobNumber || job?.job_number || job?.id?.slice(0, 8).toUpperCase()}
                  </Badge>
                  <Badge className="bg-white/90 text-gray-900 font-bold capitalize text-sm px-3 py-1">
                    {(job?.type || job?.job_type || 'Job').replace('_', ' ')}
                  </Badge>
                  {canApply && (
                    <Badge className="bg-purple-500 text-white font-bold text-sm px-3 py-1">
                      Seeking Applicants
                    </Badge>
                  )}
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                  {job?.title || job?.job_title || 'Job Details'}
                </h1>
                <div className="flex items-center gap-2 text-white/90">
                  <Calendar className="w-5 h-5" />
                  <span className="font-semibold">
                    {formatDate(job?.jobDate || job?.date || job?.job_date)}
                  </span>
                </div>
                {job?.owner && (
                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-white/20">
                    <Avatar className="w-10 h-10 border-2 border-white/40">
                      {(job.owner.photo || job.owner.photo_url) ? (
                        <img src={job.owner.photo || job.owner.photo_url} alt={job.owner.name} className="object-cover" />
                      ) : (
                        <AvatarFallback className="bg-white/20 text-white font-bold">
                          {job.owner.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div>
                      <p className="text-white/70 text-xs">Posted by</p>
                      <p className="text-white font-semibold text-sm">{job.owner.name || 'Unknown'}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <CardContent className="p-6 space-y-6">
              {/* Quick Stats - Different display for Advanced vs Simple jobs */}
              {isAdvancedJob && job?.crew_members && job.crew_members.length > 0 ? (
                /* Advanced Job - Show role-by-role breakdown */
                <div className="space-y-4">
                  {/* Location Card */}
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-50 border border-purple-100'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                      <span className={`text-xs font-medium ${isDark ? 'text-purple-300' : 'text-purple-600'}`}>Location</span>
                    </div>
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {job?.location || job?.location_address || 'TBD'}
                    </p>
                  </div>

                  {/* Roles Breakdown */}
                  <div>
                    <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <User className="w-4 h-4" />
                      Positions & Pay Breakdown
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {job.crew_members.map((member, idx) => (
                        <div
                          key={idx}
                          className={`p-4 rounded-xl relative ${member.hired ? 'opacity-60' : ''} ${isDark ? 'bg-gradient-to-br from-blue-500/10 to-green-500/10 border border-blue-500/20' : 'bg-gradient-to-br from-blue-50 to-green-50 border border-blue-100'}`}
                        >
                          {member.hired && (
                            <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-amber-500 text-white text-xs font-bold">
                              Filled
                            </div>
                          )}
                          <div className="flex items-center justify-between mb-2">
                            <span className={`font-bold text-base capitalize ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {member.role?.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm mb-3">
                            <div className="flex items-center gap-1">
                              <Clock className={`w-3.5 h-3.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                              <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{member.hours} hrs</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <DollarSign className={`w-3.5 h-3.5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                              <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>${getCrewPayout(member.ratePerHour || 0)}/hr</span>
                            </div>
                          </div>
                          <div className={`text-center py-2 rounded-lg ${isDark ? 'bg-green-500/10' : 'bg-green-50'}`}>
                            <p className={`text-xl font-black ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                              ${getCrewPayout(member.subtotal || 0).toLocaleString()}
                            </p>
                            <p className={`text-xs font-medium ${isDark ? 'text-green-400/70' : 'text-green-600'}`}>Total Earnings</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                /* Simple Job - Original 4-card grid layout */
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-50 border border-purple-100'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                      <span className={`text-xs font-medium ${isDark ? 'text-purple-300' : 'text-purple-600'}`}>Location</span>
                    </div>
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {job?.location || job?.location_address || 'TBD'}
                    </p>
                  </div>

                  <div className={`p-4 rounded-xl ${isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-100'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      <span className={`text-xs font-medium ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>Duration</span>
                    </div>
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {job?.hours || job?.job_hours || 'TBD'} hours
                    </p>
                  </div>

                  <div className={`p-4 rounded-xl ${isDark ? 'bg-green-500/10 border border-green-500/20' : 'bg-green-50 border border-green-100'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                      <span className={`text-xs font-medium ${isDark ? 'text-green-300' : 'text-green-600'}`}>Rate/Hour</span>
                    </div>
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      ${getCrewPayout(job?.ratePerHour || job?.pay_rate || 0)}
                    </p>
                  </div>

                  <div className={`p-4 rounded-xl ${isDark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-100'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className={`w-4 h-4 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                      <span className={`text-xs font-medium ${isDark ? 'text-amber-300' : 'text-amber-600'}`}>Total Payout</span>
                    </div>
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      ${getCrewPayout(job?.total || job?.total_amount || 0)}
                    </p>
                  </div>
                </div>
              )}

              {/* Posted By - Only show for authenticated users */}
              {isAuthenticated && job?.owner && (
                <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <Avatar className="w-12 h-12">
                      {(job.owner.photo || job.owner.photo_url) ? (
                        <img src={job.owner.photo || job.owner.photo_url} alt={job.owner.name} className="object-cover" />
                      ) : (
                        <AvatarFallback className={`${isDark ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-600'}`}>
                          {job.owner.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Posted by</p>
                      <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {job.owner.name || 'Unknown'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Required Roles */}
              {job?.required_roles && job.required_roles.length > 0 && (
                <div>
                  <h3 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Briefcase className="w-4 h-4" />
                    Required Roles
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {job.required_roles.map((role, idx) => (
                      <Badge key={idx} variant="outline" className={isDark ? 'border-purple-500/30 text-purple-300' : ''}>
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              {job?.description && (
                <div>
                  <h3 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FileText className="w-4 h-4" />
                    Description
                  </h3>
                  <p className={`whitespace-pre-wrap ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {job.description}
                  </p>
                </div>
              )}


              {/* Apply Section */}
              {canApply && (
                <div className={`border-t pt-6 ${isDark ? 'border-purple-500/20' : 'border-gray-200'}`}>
                  {!isAuthenticated ? (
                    /* Not logged in - show login/signup button */
                    <div className="space-y-4">
                      <div className={`p-4 rounded-xl text-center ${isDark ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-purple-50 border border-purple-200'}`}>
                        <User className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                        <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Want to apply for this job?
                        </p>
                        <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Create a free account or log in to submit your application
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <Button
                          onClick={() => {
                            localStorage.setItem('redirectAfterLogin', `/jobs/${jobId}`);
                            navigate('/login');
                          }}
                          size="lg"
                          variant="outline"
                          className={`flex-1 py-6 ${isDark ? 'border-purple-500/30 text-white hover:bg-purple-500/10' : ''}`}
                        >
                          Log In
                        </Button>
                        <Button
                          onClick={() => {
                            localStorage.setItem('redirectAfterLogin', `/jobs/${jobId}`);
                            navigate('/signup');
                          }}
                          size="lg"
                          className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-6"
                        >
                          Sign Up to Apply
                        </Button>
                      </div>
                    </div>
                  ) : hasApplied ? (
                    <div className={`p-4 rounded-xl text-center ${isDark ? 'bg-green-500/10 border border-green-500/30' : 'bg-green-50 border border-green-200'}`}>
                      <CheckCircle className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                      <p className={`font-semibold ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                        You've already applied to this job
                      </p>
                      <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Check your applications for updates
                      </p>
                      <Button
                        variant="outline"
                        className="mt-3"
                        onClick={() => navigate('/applications')}
                      >
                        View My Applications
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={handleApplyClick}
                      size="lg"
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-6"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      Apply for this Job
                    </Button>
                  )}
                </div>
              )}

              {/* Job Closed Message */}
              {!canApply && (
                <div className={`p-4 rounded-xl text-center ${isDark ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
                  <p className={`font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    This job is no longer accepting applications
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>

      {/* Apply Job Modal */}
      {showApplyModal && job && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[80] p-0 sm:p-4">
          <div
            className={`${isDark ? 'bg-[#2a2240]' : 'bg-white'} w-full h-full sm:h-auto sm:rounded-2xl shadow-2xl sm:max-w-2xl overflow-hidden`}
            style={{ maxHeight: '90vh' }}
          >
            {/* Header */}
            <div className={`${isDark ? 'bg-gradient-to-r from-[#7B3FF2] to-[#9059ff]' : 'bg-gradient-to-r from-[#7B3FF2] to-[#9059ff]'} px-4 sm:px-6 py-5 border-b ${isDark ? 'border-[#7B3FF2]/20' : 'border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <Send className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                    Apply for {job.title}
                  </h3>
                  <p className="text-sm text-white/80">
                    Submit your application with an optional introduction
                  </p>
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div
              className="overflow-y-auto px-4 sm:px-6 py-4 space-y-4"
              style={{ maxHeight: 'calc(90vh - 200px)' }}
            >
              {/* Job Summary Card */}
              <div className={`${isDark ? 'bg-[#1e1534]' : 'bg-gradient-to-br from-purple-50 to-blue-50'} rounded-xl p-4 border ${isDark ? 'border-purple-500/20' : 'border-purple-200'}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {job.title}
                    </h4>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                      📅 {formatDate(job.jobDate || job.date)}
                    </p>
                    {job.location && (
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                        📍 {job.location}
                      </p>
                    )}
                  </div>
                  <Badge className="bg-[#7B3FF2] text-white">
                    #{job.id?.substring(0, 6).toUpperCase()}
                  </Badge>
                </div>

                <div className={`pt-3 border-t ${isDark ? 'border-purple-500/20' : 'border-purple-200'}`}>
                  <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    💰 <span className="font-semibold">${getCrewPayout(job.ratePerHour || job.pay_rate || 0)}/hr</span> × {job.hours || 0} hours
                  </p>
                  <p className={`text-lg font-bold mt-2 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    Total: ${getCrewPayout(job.total || job.total_amount || 0).toLocaleString()}
                    <span className={`text-xs font-normal ml-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      (Your payout if hired)
                    </span>
                  </p>
                </div>
              </div>

              {/* Role Selection for Advanced Jobs */}
              {isAdvancedJob && job?.crew_members && job.crew_members.length > 0 && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Which position are you applying for? <span className="text-red-400">*</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {job.crew_members.filter(member => !member.hired).map((member, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setSelectedRole(member.role)}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          selectedRole === member.role
                            ? isDark
                              ? 'border-[#7DD3C0] bg-[#7DD3C0]/10 text-white'
                              : 'border-[#7DD3C0] bg-[#7DD3C0]/10 text-gray-900'
                            : isDark
                              ? 'border-purple-500/20 hover:border-purple-500/40 text-gray-300'
                              : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold capitalize">{member.role?.replace(/_/g, ' ')}</span>
                          <span className={`text-sm ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                            ${getCrewPayout(member.subtotal || 0)}
                          </span>
                        </div>
                        <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {member.hours} hrs @ ${getCrewPayout(member.ratePerHour || 0)}/hr
                        </div>
                      </button>
                    ))}
                  </div>
                  {!selectedRole && (
                    <p className={`text-xs mt-2 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                      Please select a position to continue
                    </p>
                  )}
                </div>
              )}

              {/* Introduction Message */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Introduce Yourself <span className="text-gray-400">(Optional)</span>
                </label>
                <Textarea
                  value={applicationMessage}
                  onChange={(e) => setApplicationMessage(e.target.value)}
                  placeholder="Share why you're a great fit for this job, your relevant experience, or any questions you have..."
                  rows={3}
                  className={`w-full resize-none rounded-xl border p-3 text-sm ${
                    isDark
                      ? 'bg-[#1e1534] border-purple-500/20 text-white placeholder-gray-500'
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                  } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                />
                <p className={`text-xs mt-1.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  💡 A personalised message can help you stand out from other applicants
                </p>
              </div>

              {/* Notify if unsuccessful */}
              <label
                className={`flex items-start gap-3 cursor-pointer rounded-xl border p-3 ${
                  isDark
                    ? 'bg-[#1e1534] border-purple-500/20'
                    : 'bg-gray-50 border-gray-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={notifyIfUnsuccessful}
                  onChange={(e) => setNotifyIfUnsuccessful(e.target.checked)}
                  className="mt-0.5 h-5 w-5 rounded border-gray-300 text-purple-500 focus:ring-purple-500 cursor-pointer"
                />
                <span>
                  <span className={`block text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    Notify me if I'm unsuccessful
                  </span>
                  <span className={`block text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    We'll email you if this role is filled or closed and you're not selected.
                  </span>
                </span>
              </label>
            </div>

            {/* Action Buttons - Sticky Footer */}
            <div className={`sticky bottom-0 ${isDark ? 'bg-[#2a2240]' : 'bg-white'} border-t ${isDark ? 'border-purple-500/20' : 'border-gray-200'} px-4 sm:px-6 py-4`}>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => setShowApplyModal(false)}
                  variant="outline"
                  disabled={submittingApplication}
                  className={`flex-1 h-12 ${isDark ? 'border-purple-500/30 hover:bg-purple-500/10' : 'border-gray-300'}`}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitApplication}
                  disabled={submittingApplication || (isAdvancedJob && job?.crew_members?.length > 0 && !selectedRole)}
                  className="flex-1 h-12 bg-gradient-to-r from-[#7DD3C0] to-[#5bb3a0] hover:from-[#6bc3b0] hover:to-[#4aa390] text-black font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingApplication ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Application
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[90] p-4">
          <div className={`${isDark ? 'bg-[#2a2240]' : 'bg-white'} rounded-2xl shadow-2xl max-w-md w-full p-8 text-center`}>
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>

            <h3 className={`text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Space Grotesk' }}>
              Application Submitted! 🎉
            </h3>

            <p className={`text-base mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Your application has been successfully submitted. The job owner will review your profile and may reach out soon.
            </p>

            <div className={`${isDark ? 'bg-[#1e1534]' : 'bg-gray-50'} rounded-xl p-4 mb-6`}>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                💡 <strong>Tip:</strong> Keep your profile updated and check your messages regularly for updates from job owners.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => setShowConfirmationModal(false)}
                variant="outline"
                className={`flex-1 ${isDark ? 'border-purple-500/30 hover:bg-purple-500/10' : ''}`}
              >
                Stay Here
              </Button>
              <Button
                onClick={() => navigate('/applications')}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
              >
                View My Applications
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Blocker Modal */}
      <ProfileBlockerModal
        isOpen={showProfileBlocker}
        onClose={() => setShowProfileBlocker(false)}
        userStatus={userStatus}
        isDark={isDark}
        userId={user?.id}
      />

      {/* Cancellation Payment Blocker Modal */}
      <CancellationPaymentBlockerModal
        isOpen={showCancellationBlocker}
        outstandingFees={outstandingCancellationFees}
      />
    </div>
  );
};

export default JobDetails;
